import { PermissionsConstants, ScopeType } from 'src/common/types';
import DataStore, { Query } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';

import {
  CheckDatabaseLog,
  CheckDatabaseLogResultItem,
  CreateOrganizationItem,
  DatabaseInconsistencyLog,
  DatabaseInconsistencyLogResultItem,
  OrganizationBackupItem,
  OrganizationDatabaseStatusItem,
  OrganizationItem,
  OrganizationMemberItem,
  OrganizationSummary,
  reportHeader,
} from 'src/stores/organizations-store';
import { OrganizationsSummaryMockData } from '../data-sources/organization-summary';
import { OrganizationItemMockData } from '../data-sources/organization-item';
import { OrganizationDbLogResultMockData, OrganizationDbLogSingleResultMockData } from '../data-sources/organization-db-log-result';
import { OrganizationDbLogsMockData } from '../data-sources/organization-db-logs';
import DataStoreMock from './base/data-store';
import { OrganizationBackupsMockData } from '../data-sources/organization-backups';
import FormStoreMock from './base/form-store';
import { OrganizationDbInconsistencyLogsResultMockData } from '../data-sources/organization-db-inconsistency-log-results';
import { OrganizationDbInconsistencyLogsMockData } from '../data-sources/organization-db-inconsistency-logs';
import { CommandResult } from 'src/core/stores/types';

export class OrganizationSummaryStoreMock extends DataStore<OrganizationSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations`, []);
  }

  public async load(_query: Partial<Query>) {
    this._state.set({
      isBusy: false,
      errorMessage: undefined,
      items: OrganizationsSummaryMockData,
      count: OrganizationsSummaryMockData.length,
    });
    return true;
  }

  public async getItems(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      return OrganizationsSummaryMockData;
    });
  }
  public async getUserPartnerOrganizations(permission: PermissionsConstants, query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      return OrganizationsSummaryMockData;
    });
  }
  public async delete(id: string, params?: any) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async organizationExists(id: string) {
    return true;
  }
}

export class OrganizationDatabaseLogsStoreMock extends DataStoreMock<CheckDatabaseLog> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/check-db-log`);
  }

  public async load(_query: Partial<Query>) {
    this._state.set({
      isBusy: false,
      items: OrganizationDbLogsMockData,
      count: OrganizationDbLogsMockData.length,
    });
    return true;
  }

  public async registerLog(item: CheckDatabaseLog) {
    return { isSuccess: true, error: '', messages: [] };
  }
}

export class OrganizationDatabaseLogsResultStoreMock extends DataStoreMock<CheckDatabaseLogResultItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/check-db-log`);
  }

  public async getLogData(id: string) {
    return OrganizationDbLogResultMockData[0];
  }

  public async loadById(id: string, query: Partial<Query>) {
    this._state.set({
      isBusy: false,
      items: OrganizationDbLogResultMockData as any[],
      count: OrganizationDbLogResultMockData.length,
    });
    return true;
  }
}

export class OrganizationDatabaseInconsistencyLogStoreMock extends DataStoreMock<DatabaseInconsistencyLog> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/db-inconsistencies-logs`);
  }

  public async load(_query: Partial<Query>) {
    this._state.set({
      isBusy: false,
      items: OrganizationDbInconsistencyLogsMockData,
      count: OrganizationDbInconsistencyLogsMockData.length,
    });
    return true;
  }
}

export class OrganizationDatabaseInconsistencyLogResultStoreMock extends DataStoreMock<DatabaseInconsistencyLogResultItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/db-inconsistencies-logs`);
  }

  public async loadById(id: string, query: Partial<Query>) {
    this._state.set({
      isBusy: false,
      items: OrganizationDbInconsistencyLogsResultMockData as any[],
      count: OrganizationDbInconsistencyLogsResultMockData.length,
    });
    return true;
  }
}

export class OrganizationItemStoreMock extends FormStore<OrganizationItem, CreateOrganizationItem> {
  private exportingStatus = true;

  public async load(id: string): Promise<any> {
    this._state.set({
      isBusy: false,
      errorMessage: undefined,
      item: OrganizationItemMockData,
    });
    return OrganizationItemMockData;
  }

  public async getOrganizationData(id: string) {
    return await this.handleCallAsync(async () => {
      return OrganizationItemMockData;
    });
  }

  public async switchMFA(id: string, newValue: boolean) {
    return { isSuccess: true, error: '', messages: [] };
  }

  OrganizationMembersBasePath = 'users';
  RestartOrganizationCreationFlowBasePath = 'restart-creation-flow';
  ChangeBlockStateBasePath = 'change-block-state';
  sendInvitationPath = 'send-invitation';
  checkOrganizationDatabasePath = 'check-db';
  fixScopesSyncIssuesPath = 'fix-scopes-sync-issues';

  public async create(item: CreateOrganizationItem) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async fixScopesSyncIssues(orgId: string, justCheck = false, scopeType: ScopeType | null = null) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async restartOrganizationCreationFlow(id: string) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async checkOrganizationDatabase(id: string) {
    return { isSuccess: true, message: '' };
  }

  public async checkAllOrganizationsDatabases() {
    this._state.isBusy.set(true);
    return new Promise<CommandResult<{}> & { message: string }>((resolve) => {
      setTimeout(() => {
        this._state.set({
          isBusy: false,
          item: {} as any,
        });
        resolve({ isSuccess: true, error: '', messages: [], item: OrganizationDbLogSingleResultMockData, message: '' });
      }, 500);
    });
  }

  public async getCheckOrganizationsReportPDF(items: OrganizationDatabaseStatusItem[], userName: string, createdOn: Date, isSuccess: boolean) {
    return true;
  }

  public async addOrganizationMembers(id: string, userIdOrEmails: OrganizationMemberItem[]) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async deleteOrganizationMembers(id: string, userIds: string[]) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async changeOrganizationBlockStatus(id: string, isBlocked: boolean) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async checkExportingStatus(id: string) {
    return { count: 0, items: [] };
  }

  public async checkLockedStatus(id: string) {
    return false;
  }

  public async sendInvitation(id: string, userId: string) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async exportFile(fileName: string) {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async importFile() {
    return { isSuccess: true, error: '', messages: [] };
  }

  public async getCheckOrganizationsReportCSV(items: OrganizationDatabaseStatusItem[], headers: reportHeader[]) {
    return true;
  }
}

export class OrganizationImportStoreMock extends FormStoreMock<any, any> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/export-import/api/backup`);
  }

  public async uploadOrganizationBackup(file: File) {
    return { isSuccess: true, error: '', messages: [] };
  }
  public async exportFile(organizationId: string, fileName: string) {
    return { isSuccess: true, error: '', messages: [] };
  }
}

export class OrganizationBackupStoreMock extends DataStoreMock<OrganizationBackupItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations-backup`);
  }

  public async loadSelected(query: Partial<Query>, selectedIds: string[]) {
    return true;
  }

  public async getByOrganizationId(organizationId: string) {
    return OrganizationBackupsMockData;
  }

  public async restoreOrganization(backupId: string) {
    return { isSuccess: true, message: '' };
  }

  public async generateDownloadUrl(backupId: string, organizationId: string) {
    return { isSuccess: true, url: '' };
  }
}
